/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
} from 'antd';
import { formatReceivedValues } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import { getSubtiposNominas } from 'api/nominas/subtiposDeNominas';
import {
  getDeduccionesSubtiposDeNominas,
  postDeduccionSubtipoDeNomina,
  deleteDeduccionSubtipoDeNomina,
  patchDeduccionSubtipoDeNomina,
  getDeduccionSubtipoDeNomina,
} from 'api/nominas/deducciones/deduccionesSubtiposDeNominas';
import { checkToken } from 'api/utils';

export const deduccionSubtipoNominaPermission = {
  permissionModel: 'deduccionsubtipodenomina',
  catalogsModels: [],
  tabsModels: [],
};

const DeduccionSubtipoDeNomina = ({
  permission,
  selected,
  estatusInactivo,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [subtipos, setSubtipos] = useState([]);

  const formInstances = [form];

  const formatReceivedClone = (values) => {
    if (Array.isArray(values)) {
      return values.map((e) => ({
        ...formatReceivedValues(e),
        estado_global: formatReceivedValues(e)?.estados_globales,
      }));
    }
    return formatReceivedValues(values);
  };

  const fetchData = async () => {
    setLoading(true);
    const _data = await getDeduccionesSubtiposDeNominas({ deduccion: selected });
    setData(formatReceivedClone(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [_estados, _subtipos, _data] = await Promise.all([
        getEstadosGlobales(),
        getSubtiposNominas(),
        getDeduccionesSubtiposDeNominas({ deduccion: selected }),
      ]);
      if (mounted) {
        setEstados(_estados);
        setSubtipos(_subtipos);
        setData(formatReceivedClone(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _record = await getDeduccionSubtipoDeNomina(match.id);
    setTimeout(() => {
      if (_record) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceivedClone(_record));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = { ...form.getFieldsValue() };
      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchDeduccionSubtipoDeNomina(key, { ...values, deduccion: selected })
        : postDeduccionSubtipoDeNomina({ ...values, deduccion: selected }));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteDeduccionSubtipoDeNomina(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const columns = [
    {
      titleText: 'Subtipo de Nómina',
      dataIndex: 'subtipo_de_nomina',
      key: 'subtipo_de_nomina',
      width: 200,
      render: (val) => subtipos.find((e) => e.id === val)?.descripcion,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estado_global',
      key: 'estado_global',
      width: 150,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const rules = {
    required: [{ required: true, message: 'El campo es requerido' }],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete: () => setVisibleAlert(true),
          }}
          disableAdd={estatusInactivo}
          disableDelete={estatusInactivo}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Subtipo de Nómina`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
          width="40%"
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="subtipo_nomina"
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="subtipo_de_nomina"
                label="Subtipo de Nómina"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  labelProp="descripcion"
                  dataSource={subtipos}
                  disabled={estatusInactivo}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={rules.required}
                hasFeedback
                initialValue={1}
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  disabled={!selectedRowKeys.length || estatusInactivo}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Subtipo de Nómina ${subtipos.find((e) => e.id === form.getFieldValue('subtipo_de_nomina'))?.descripcion}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default DeduccionSubtipoDeNomina;
